import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import { useIntl } from 'react-intl'
import pick from 'lodash/pick'
import { Col, Row } from 'antd'

import RequestStepper from 'components/ContactRequestFormComponents/Stepper/RequestStepper'
import { setCurrentUser } from 'store/modules/CurrentUser'
import { sanitizeFormValues, showSanitizedErrors } from 'components/ContactRequestFormComponents/helpers'

import { getGoogleAnalyticsTracker } from 'helpers/googleAnalytics'
import { parseBudgetRange } from 'helpers/number'
import { closeErrorNotifications } from 'helpers/errors'
import classNames from 'helpers/css'

import Form from '../components/Form'

import styles from './index.module.scss'

import {
  CREATE_USER_MUTATION,
  CREATE_PROJECT_MUTATION,
  CREATE_CONTACT_REQUEST_MUTATION,
} from '../graphql'

const InitialContactRequest = (props) => {
  const {
    provider,
    initialData,
    onSuccessCallback,
  } = props

  const dispatch = useDispatch()
  const intl = useIntl()
  const [createUser, { loading: userSubmitting }] = useMutation(CREATE_USER_MUTATION)
  const [createProject, { loading: projectSubmitting }] = useMutation(CREATE_PROJECT_MUTATION)
  const [createContactRequest, { loading: contactRequestSubmitting }] = useMutation(CREATE_CONTACT_REQUEST_MUTATION)

  const submitting = userSubmitting || projectSubmitting || contactRequestSubmitting

  const setUserHasAccessToken = () => dispatch(setCurrentUser({ hasAccessToken: true }))

  const submitForm = async (formData) => {
    const success = await createUser({ variables: pick(formData, ['company', 'name', 'email', 'phone']) })
      .then((_userResult) => {
        const budgetRange = parseBudgetRange(formData.eventBudget)

        return createProject({
          variables: {
            ...pick(formData, ['email', 'roomType', 'requestedDates', 'cityId']),
            guestCount: parseInt(formData.numberOfPersons),
            eventCategoryId: provider.eventCategories?.find(
              category => category.name === formData.eventCategory,
            )?.id || formData.eventCategoryId,
            budgetMax: budgetRange[1],
            providerId: provider.id,
          },
        })
      }).then((projectResult) => {
        const ga = getGoogleAnalyticsTracker()

        return createContactRequest({
          variables: {
            providerId: provider.id,
            projectId: projectResult?.data?.createProject?.project?.id,
            consent: formData.consent,
            newsletter: formData.newsletter,
            googleAnalyticsClientId: ga && ga.get('clientId'),
            forNewProject: true,
          },
        })
      }).then(({ data }) => {
        onSuccessCallback(data.createContactRequest.contactRequest)

        return true
      }).catch((errors) => {
        showSanitizedErrors(
          errors.graphQLErrors[0]?.extensions?.errors,
          intl,
        )

        return false
      })

    if (success) {
      setUserHasAccessToken()
    }
  }

  const submitFormAction = (values) => {
    closeErrorNotifications()
    submitForm(sanitizeFormValues(values))
  }

  return (
    <>
      <Row gutter={32}>
        <Col sm={{ span: 16, offset: 8 }} >
          <div className={classNames(styles, 'contact-request__initial__stepper')}>
            <RequestStepper step={1} />
          </div>
        </Col>
      </Row>

      <Form
        provider={provider}
        submitForm={submitFormAction}
        isSubmitting={submitting}
        initialData={initialData}
        headlineTranslationKey="contactRequest.headline.rfp"
      />
    </>
  )
}

InitialContactRequest.propTypes = {
  initialData: PropTypes.object,
  onSuccessCallback: PropTypes.func.isRequired,
  provider: PropTypes.shape({
    id: PropTypes.string.isRequired,
    eventCategories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
    })),
  }).isRequired,
}

export default InitialContactRequest
